import { Component, DestroyRef, EventEmitter, Inject, inject, Input, Output } from '@angular/core';
import { ConfigConstants } from '../../config/config-constants';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';

@Component({
    selector: 'pace-core-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent {
    @Input() public allowedMimeTypes: string[] = [];
    @Input() public files: File[] = [];
    @Output() public filesChange = new EventEmitter<File[]>();

    public thumbnails: Array<{ file: File; thumbnail: string }> = [];
    public displayNames: File[] = [];
    public fileOver = false;
    public showWarning = false;
    public cameraGranted = false;
    public sizeAllowed = ConfigConstants.FileUploadSizeAllowed;
    private readonly SUPPORTED_THUMBNAIL_TYPES = ['image/png', 'image/tiff', 'image/tiff-fx', 'image/jpeg'];
    private destroyRef = inject(DestroyRef);

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
    ) {}

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.emitFiles(input.files);

        /* Reset input value so that selecting the same file again triggers a new change event.
            Without this, browsers won't emit a change event when re-selecting the same file. */

        input.value = '';
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.fileOver = true;
    }

    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.fileOver = false;
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        this.fileOver = false;
        const files = event.dataTransfer?.files;
        if (files) {
            this.emitFiles(files);
        }
    }

    public emitFiles(fileList: FileList | null | undefined): void {
        if (!fileList) {
            return;
        }

        Array.from(fileList).forEach((file) => this.handleFile(file));
        this.filesChange.emit(this.files);
    }

    private handleFile(file: File): void {
        if (this.files.find((f) => f.name === file.name && f.size === file.size)) {
            return;
        }
        if (this.calculateTotalSize(file.size) >= this.sizeAllowed) {
            this.showWarning = true;
            console.warn('[SizeExceeded]', file.name);
            return;
        }

        this.showWarning = false;
        this.files.push(file);

        if (this.SUPPORTED_THUMBNAIL_TYPES.includes(file.type)) {
            this.createThumbnail(file);
        } else {
            this.displayNames.push(file);
        }
    }

    private createThumbnail(file: File): void {
        const reader = new FileReader();
        reader.onload = (): void => {
            const thumbnail: string = typeof reader.result === 'string' ? reader.result : '';
            this.thumbnails.push({ thumbnail, file });
        };
        reader.readAsDataURL(file);
    }

    private calculateTotalSize(currentFileSize = 0): number {
        return this.files.reduce((acc, file) => acc + file.size, currentFileSize);
    }

    public removeThumbnail(index: number): void {
        const file = this.thumbnails[index].file;
        this.thumbnails.splice(index, 1);
        this.files = this.files.filter((f) => !(f.name === file.name && f.size === file.size));
        this.filesChange.emit(this.files);
    }

    public removeDisplayName(index: number): void {
        const file = this.displayNames[index];
        this.displayNames.splice(index, 1);
        this.files = this.files.filter((f) => !(f.name === file.name && f.size === file.size));
        this.filesChange.emit(this.files);
    }
}
